import { useState, useEffect, useContext, useRef } from "react";
import {
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { getTablePagination } from "../../../utils/tablePagination";
import { getRandomColor, userRoles } from "../../../helpers/utility";
import { getRoleLabel } from "../../../helpers/getRoleLabel";
import tagStyles from "../../../styles/Custom/Tag.module.css";
import { QuestionCircleOutlined, StopOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import UsersForm from "./UsersForm";
import { deactivateUserApi } from "../../../services/auth.services";
import { getFacilityListApi } from "../../../services/facilities.services";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { Role } from "../../../constants/defaultKeys";
import useQuery from "../../../hooks/useQuery";
import useIsMobile from "../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../hooks/usePageFillTableScroll";

const USERS_PAGE_SIZE = 25;

const UsersTable = ({
  data,
  loading,
  refreshPage,
  isReceptionist,
  page,
  setPage,
  totalCount,
}) => {
  const { userDetails } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [editListData, setEditListData] = useState(null);
  const [facilities, setFacilities] = useState(null);

  const defaultRole = userDetails?.currRole || getCookie(Role);

  const selectedRole = useQuery()?.role;
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(
    tableWrapRef,
    isMobile,
    "default",
    Array.isArray(data) ? data.length : 0
  );

  const onDeactivate = (id) => {
    deactivateUserApi({ user_id: id })
      .then((res) => {
        message?.success(res?.data?.message);
        refreshPage();
      })
      .catch((err) => {
        message?.error(err?.response?.data?.message);
      });
  };
  useEffect(() => {
    getFacilityListApi()
      .then((res) => {
        setFacilities(res?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);
  const roleFilters = userRoles?.map((role) => {
    return { text: role?.label, value: role?.value };
  });

  const roleEditPermission = (defaultRoles) => {
    const role = defaultRoles?.map((item) => item?.role);

    const editRole =
      //user is facility admin doesn't have permission to edit org admin and org owner
      defaultRole === "Facility_Admin"
        ? !(
            role?.includes("Organization_Admin") ||
            role?.includes("Organization_Owner")
          )
        : //user is org admin doesn't have permission to edit org owner
        defaultRole === "Organization_Admin"
        ? !role?.includes("Organization_Owner")
        : true;

    return editRole;
  };

  const columns = [
    {
      title: "First Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "10%",
      sorter: (a, b) => a.first_name.localeCompare(b.first_name),
    },
    {
      title: "Last Name",
      dataIndex: "last_name",
      key: "last_name",
      width: "10%",
      sorter: (a, b) => a.last_name.localeCompare(b.last_name),
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
      width: "10%",
      sorter: (a, b) => a.mobile - b.mobile,
    },

    {
      title: "Role",
      dataIndex: "roles",
      key: "roles",
      width: "20%",
      filters: [...roleFilters],
      render: (value) =>
        value
          ? value?.map((role, idx) => (
              <Typography key={role?.org_user_id ?? idx}>
                {getRoleLabel(role)}
              </Typography>
            ))
          : "-",
      onFilter: (value, record) =>
        record.roles.some((role) => role.role === value),
    },
    {
      title: "Facility",
      dataIndex: "facilities",
      key: "facilities",
      width: "20%",
      // filters: facilities && [...facilityFilters],
      render: (value) => {
        return (
          <>
            {
              new Set(
                value?.map((facility) => {
                  return facility?.facility ? (
                    <Tag
                      style={{ marginBottom: "4px" }}
                      key={facility?.facility}
                      className={tagStyles.mdRndTag}
                      color={getRandomColor(facility?.facility)}
                    >
                      {facility?.facility}
                    </Tag>
                  ) : null;
                })
              )
            }
          </>
        );
      },
      // onFilter: (value, record) =>
      //   record.facilities.some((facility) => facility.facility === value),
    },
    ...(isReceptionist ? [] : [
    {
      title: "Action",
      width: "5%",
      render: (_, record) => (
        <Space>
          {roleEditPermission(record?.roles) ? (
            <Space>
              {" "}
              <EditIcon
                onClick={() => {
                  setOpen(true);
                  setEditListData(record);
                }}
              />
              <Popconfirm
                title="Deactivate the user"
                description="User would loose access to the app"
                okText="Yes"
                cancelText="No"
                icon={
                  <QuestionCircleOutlined
                    style={{
                      color: "red",
                    }}
                  />
                }
                onConfirm={() => onDeactivate(record?.user_id)}
              >
                <StopOutlined />
              </Popconfirm>
            </Space>
          ) : null}
        </Space>
      ),
    },
    ])
  ];
  const table = (
    <div ref={tableWrapRef} className="ui-table-fill-body">
      <Table
        className="no-vertical-borders"
        key={`user-table-${selectedRole}`}
        rowKey={(record) => record.user_id ?? record.id}
        columns={columns}
        dataSource={data}
        pagination={getTablePagination({
          current: page,
          pageSize: USERS_PAGE_SIZE,
          total: totalCount ?? 0,
          onChange: (p) => setPage(p),
        })}
        loading={loading}
        scroll={tableScroll}
      />
    </div>
  );

  return (
    <>
      {table}
      {open && (
        <UsersForm
          isVisible={open}
          setIsVisible={setOpen}
          editListData={editListData}
          refreshPage={refreshPage}
        />
      )}
    </>
  );
};

export default UsersTable;
